import React, { useState, useEffect } from 'react';
import { Stoplight } from './Stoplight';

interface ClientHealthProps {
  projectId: string;
  className?: string;
}

interface HealthData {
  timeline: {
    phaseSlipDays: number;
  };
  clientTasks: {
    overdue: number;
  };
  files: {
    verifiedPercentage: number;
  };
  support: {
    p1Count: number;
    p2Count: number;
  };
}

type HealthStatus = 'green' | 'amber' | 'red' | 'neutral';

export const ClientHealth: React.FC<ClientHealthProps> = ({ projectId, className = '' }) => {
  const [status, setStatus] = useState<HealthStatus>('neutral');
  const [asOfTime, setAsOfTime] = useState<string>('—');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHealthData();
  }, [projectId]);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      
      const healthData: Partial<HealthData> = {
        timeline: { phaseSlipDays: 2 },
        clientTasks: { overdue: 1 },
        files: { verifiedPercentage: 75 },
        support: { p1Count: 0, p2Count: 1 }
      };

      const computedStatus = computeHealthStatus(healthData);
      setStatus(computedStatus);

      const now = new Date();
      setAsOfTime(now.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' at ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

    } catch (error) {
      console.error('Failed to fetch health data:', error);
      setStatus('neutral');
      setAsOfTime('—');
    } finally {
      setLoading(false);
    }
  };

  const calculatePhaseSlip = (timeline: any): number => {
    if (!timeline.phases) return 0;
    
    let maxSlip = 0;
    const now = new Date();
    
    timeline.phases.forEach((phase: any) => {
      if (phase.status === 'in-progress' || phase.status === 'overdue') {
        const endDate = new Date(phase.endDate);
        if (now > endDate) {
          const slipDays = Math.ceil((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
          maxSlip = Math.max(maxSlip, slipDays);
        }
      }
    });
    
    return maxSlip;
  };

  const computeHealthStatus = (data: Partial<HealthData>): HealthStatus => {
    if (
      (data.support?.p1Count || 0) > 0 ||
      (data.clientTasks?.overdue || 0) >= 3 ||
      (data.files?.verifiedPercentage || 100) < 60 ||
      (data.timeline?.phaseSlipDays || 0) > 5
    ) {
      return 'red';
    }

    if (
      (data.support?.p2Count || 0) >= 1 ||
      ((data.clientTasks?.overdue || 0) >= 1 && (data.clientTasks?.overdue || 0) <= 2) ||
      ((data.files?.verifiedPercentage || 100) >= 60 && (data.files?.verifiedPercentage || 100) <= 89) ||
      ((data.timeline?.phaseSlipDays || 0) >= 1 && (data.timeline?.phaseSlipDays || 0) <= 5)
    ) {
      return 'amber';
    }

    return 'green';
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-between ${className}`}>
        <div className="w-24 h-6 bg-gray-300 animate-pulse rounded"></div>
        <span className="text-xs text-slate-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <Stoplight 
        status={status} 
        srLabel={`Overall Health: ${status.charAt(0).toUpperCase() + status.slice(1)} (as of ${asOfTime})`}
      />
      <span className="text-xs text-slate-600">Project Health as of {asOfTime}</span>
    </div>
  );
};
